import { LitElement, html } from "lit";
import { property, state } from "lit/decorators.js";
import { customElement } from "lit/decorators.js";

@customElement("sb-sidedrawer")
export class SbSideDrawer extends LitElement {
  @property({ type: String }) drawer_id: string | undefined;
  @property({ type: Boolean }) inplace = false;
  @property({ type: String }) side: "left" | "right" = "left";
  @property({ type: Boolean, reflect: true }) open = false;
  @property({ type: Boolean }) closeOnOverlay = true;
  private _cachedChildren: Node[] = [];

  @state() private _mounted = false;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  constructor() {
    super();
    this._handleOpen = this._handleOpen.bind(this);
    this._handleClose = this._handleClose.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    if (this._cachedChildren.length === 0) {
      this._cachedChildren = Array.from(this.childNodes);
      this._cachedChildren.forEach((node) => this.removeChild(node));
    }
    window.addEventListener("open-sidedrawer", this._handleOpen);
    window.addEventListener("close-sidedrawer", this._handleClose);
    document.addEventListener("open-sidedrawer", this._handleOpen);
    document.addEventListener("close-sidedrawer", this._handleClose);
    this._mounted = true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("open-sidedrawer", this._handleOpen);
    window.removeEventListener("close-sidedrawer", this._handleClose);
    document.removeEventListener("open-sidedrawer", this._handleOpen);
    document.removeEventListener("close-sidedrawer", this._handleClose);
    this._mounted = false;
  }

  private _handleOpen(e?: Event) {
    const detail: any = (e as CustomEvent)?.detail || {};
    // Support both `id` and `drawer_id` in the event detail.
    const targetId = detail.drawer_id ?? detail.id;
    const selfId = this.drawer_id ?? (this as HTMLElement).id;

    // If this drawer doesn't have an ID, ignore the event
    if (!selfId) return;

    // If the event has a target ID and it doesn't match this drawer, ignore
    if (targetId && targetId !== selfId) return;

    // If the event has NO target ID, we assume it's meant for this drawer
    // (for cases where only one drawer exists)

    if (detail.side && (detail.side === "left" || detail.side === "right")) {
      this.side = detail.side;
    }
    this.open = true;
    this.dispatchEvent(
      new CustomEvent("sidedrawer-opened", {
        bubbles: true,
        detail: { id: selfId },
      }),
    );
  }

  private _handleClose(e?: Event) {
    const detail: any = (e as CustomEvent)?.detail || {};
    const targetId = detail.drawer_id ?? detail.id;
    const selfId = this.drawer_id ?? (this as HTMLElement).id;

    if (!selfId) return;
    if (targetId && targetId !== selfId) return;

    this.open = false;
    this.dispatchEvent(
      new CustomEvent("sidedrawer-closed", {
        bubbles: true,
        detail: { id: selfId },
      }),
    );
  }

  private _onOverlayClick() {
    if (this.closeOnOverlay) this._handleClose();
  }

  render() {
    const sideClass =
      this.side === "right" ? "sb-sidedrawer-right" : "sb-sidedrawer-left";
    return html`
      <div
        part="overlay"
        class="sb-sidedrawer-overlay ${this.inplace ? "inplace" : ""} ${this
          .open
          ? "open"
          : "closed"}"
        @click=${this._onOverlayClick}
      ></div>
      <aside
        part="sheet"
        class="sb-sidedrawer ${sideClass} ${this.inplace
          ? "inplace"
          : ""} ${this.open ? "open" : "closed"}"
        role="complementary"
        aria-hidden=${!this.open}
      >
        <div class="sb-sidedrawer-content" part="content">
          ${this._cachedChildren}
        </div>
      </aside>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sb-sidedrawer": SbSideDrawer;
  }
}
